"use strict";

/**
 * Defines a "class" that sends logged DDUX events to via a backend MATLAB.
 * The class offers the following methods required for any "Sender":
 *
 *    start
 *    stop
 *    isRunning
 *    sendEvent
 *
 * @copyright 2018-2021 The MathWorks, Inc.
 */
define(["dojo/_base/lang", "dojo/Deferred", "mw-log/Log", "mw-messageservice/MessageService", "mw-utils/Utils"], function (lang, Deferred, Log, MessageService, Utils) {
  var _started;

  var _channel;

  var _responseChannel;

  var _responseSequence = 0;
  var _awaitingResponses = [];
  var USAGE_SETUP_CHANNEL = "/UsageSetup";
  var USAGE_DATA_CHANNEL = "/UsageData";
  var USAGE_RESPONSE_CHANNEL = "/UsageResponse";
  return {
    /**
     * Initializes communication with the backend MATLAB
     *
     * @param loggerProperties {object} supplies default values for certain event attributes.
     * @param loggerProperties.product {string} identifies the product for all UI event and
     *        telemetry logs unless overriden
     * @param loggerProperties.scope {string} identifies the scope for all UI event logs
     *        unless overridden.
     * @param loggerProperties.applicationName {string} the name of the application logging data
     * @param loggerProperties.applicationVersion {string} the version of the application logging data
     *
     * @instance
     */
    start: function start(loggerProperties) {
      if (_started) {
        return;
      }

      _started = true;
      _channel = USAGE_DATA_CHANNEL + "/" + Utils.generateUuid();
      _responseChannel = USAGE_RESPONSE_CHANNEL + "/" + Utils.generateUuid();
      var message = {
        type: "start",
        product: loggerProperties.product,
        scope: loggerProperties.scope,
        channel: _channel,
        responseChannel: _responseChannel,
        url: location.href,
        title: document.title
      };

      if (loggerProperties.applicationName) {
        message.applicationName = loggerProperties.applicationName;
      }

      if (loggerProperties.applicationVersion) {
        message.applicationVersion = loggerProperties.applicationVersion;
      }

      MessageService.publish(USAGE_SETUP_CHANNEL, message);
      MessageService.subscribe(_responseChannel, "_handleResponse", this);
    },

    /**
     * Terminates communication with the backend MATLAB
     *
     * @instance
     */
    stop: function stop() {
      if (_started) {
        MessageService.publish(USAGE_SETUP_CHANNEL, {
          type: "stop",
          channel: _channel,
          responseChannel: _responseChannel
        });
        MessageService.unsubscribe(_responseChannel, "_handleResponse", this);
        _started = false;
      }
    },

    /**
     * Indicates whether the sender has been started and not stopped
     *
     * @returns {boolean} true if start, but not stop has been called.
     *
     * @instance
     */
    isRunning: function isRunning() {
      return _started;
    },

    /**
     * Sends an event to the MATLAB backend
     *
     * @param eventType "telemetry", "uiEvent" or "composition"
     * @param event the attributes that define the event
     * @param obtainResult true if status should be obtained from the backend
     * @returns a promise if obtainResult true was specified, otherwise undefined
     */
    sendEvent: function sendEvent(eventType, event, obtainResult) {
      //To Consider: instead of modifying event clone it and augment it
      let deferred;
      event.messageType = eventType;

      if (obtainResult) {
        event.responseSequence = _responseSequence++;
        deferred = new Deferred();
        deferred.responseSequence = event.responseSequence;

        _awaitingResponses.push(deferred);
      }

      MessageService.publish(_channel, event);

      if (deferred) {
        return deferred.promise;
      }
    },
    _handleResponse: function _handleResponse(message) {
      var sequence = message.data.responseSequence;

      while (_awaitingResponses.length > 0) {
        var awaitingResponse = _awaitingResponses.shift();

        if (awaitingResponse.responseSequence === sequence) {
          awaitingResponse.resolve(message.data.status);
          break;
        } else {
          awaitingResponse.reject();
        }
      }
    }
  };
});
//# sourceMappingURL=MatlabSender.js.map
